import React, { useState } from "react";
import { DataTable } from "@/components/ui/data-table";
import { useAttorneyContactColumn } from "./hooks";
import { Button } from "@/components/ui/button";
import { BsPlusCircleFill } from "react-icons/bs";
import { AddUpdateAttorneyContact } from "./AddUpdateAttorneyContact";
import { CustomPageSelector, CustomPagination } from "../CustomPagination";
import { CustomPaginationInfo } from "../CustomPaginationInfo";
import Loader from "@/components/ui/loader";
import { Input } from "@/components/ui/input";
import { useListApi } from "@/hooks/useListApi";
import { AttorneyContact, attorneyContactApi } from "@/api/attorneyContacts";
import { useAdminPermission, usePermission } from "@/hooks/usePermission";
import { useTranslation } from "react-i18next";
import { BulkUploadContacts } from "./BulkUploadContacts";

function AttorneyContactTable() {
  const { t } = useTranslation();
  const [selectedAttorneyContact, selectAttorneyContact] =
    useState<AttorneyContact>();
  const [openCreate, setOpenCreate] = useState<boolean>(false);
  const [openBulkUpload, setOpenBulkUpload] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const { loading, apiResult, pagination, search, refresh, order } =
    useListApi<AttorneyContact>({
      baseApiObject: attorneyContactApi,
      enable: true,
    });

  const columns = useAttorneyContactColumn({
    edit: (item: AttorneyContact) => {
      selectAttorneyContact(item);
      setOpenEdit(true);
    },
    refresh,
    setOrder: order.set,
  });
  const hasAdminPermission = useAdminPermission();
  const hasAddContactPermission = usePermission("review.add_attorneycontact");

  return (
    <div className="w-full my-8">
      <h3 className="text-3xl font-bold text-center mb-8">
        {t("contacts.title.contacts")}
      </h3>
      <div className="flex justify-start items-center gap-4">
        {hasAdminPermission && (
          <>
            <AddUpdateAttorneyContact
              open={openEdit}
              setOpen={setOpenEdit}
              attorneyContact={selectedAttorneyContact}
              refresh={refresh}
            />
            {hasAddContactPermission && (
              <AddUpdateAttorneyContact
                open={openCreate}
                setOpen={setOpenCreate}
                refresh={refresh}
                trigger={
                  <Button className="flex gap-2">
                    <BsPlusCircleFill /> {t("contacts.button.add_contact")}
                  </Button>
                }
              />
            )}
            {hasAddContactPermission && (
              <BulkUploadContacts
                open={openBulkUpload}
                setOpen={setOpenBulkUpload}
                refresh={refresh}
                trigger={
                  <Button className="flex gap-2">
                    {t("contacts.button.upload_bulk_contact")}
                  </Button>
                }
              />
            )}
          </>
        )}
      </div>
      <div className="flex gap-2 w-full my-4 md:w-[410px]">
        <Input
          value={search.value}
          type="text"
          placeholder={t("common.placeholder.search")}
          onChange={(event) => search.set(event.target.value)}
        />
        {search.value && (
          <Button onClick={() => search.set("")}>
            {t("common.button.clear")}
          </Button>
        )}
      </div>
      {loading ? (
        <div className="flex min-h-[300px] md:min-h-[450px] items-center justify-center max-h-auto">
          <Loader />
        </div>
      ) : (
        <>
          <div className="min-h-[300px] md:min-h-[450px] max-h-auto">
            <DataTable columns={columns} data={apiResult?.results || []} />
            <div className="flex flex-col md:flex-row md:justify-between">
              <div className="flex flex-col md:flex-row md:w-2/3   md:space-x-4 mt-3">
                <CustomPageSelector
                  pageSize={pagination.pageSize}
                  onPageSizeChange={(value) => {
                    pagination.setPageSize(value);
                    pagination.setPage(1);
                  }}
                />
                <CustomPaginationInfo
                  totalCount={apiResult?.count || 0}
                  pageSize={pagination.pageSize}
                  currentPage={pagination.page}
                />
              </div>
              <div className="flex md:space-x-4 mt-4 pr-5 pt-2">
                <CustomPagination
                  totalCount={apiResult?.count || 0}
                  pageSize={pagination.pageSize}
                  currentPage={pagination.page}
                  onPageChange={(value) => pagination.setPage(value)}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AttorneyContactTable;
